import { Fragment, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Dropdown, Option, Tooltip, Avatar } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { ExcelExportColumn, toDataURL } from "@progress/kendo-react-excel-export";
import dayjs from "../../../../../util/dayjs-config";
import EmptyState from "../../../../../assets/img/Errors/NoDealWithinDashboard.svg";
import EmptyStateNoDealsOnFilter from "../../../../../assets/img/Errors/NoDealsOnFilter.svg";
import { orderBy, filterBy } from "@progress/kendo-data-query";
import EditableDecimalCell from "../../../dashboard-util/dashboard-telerik-helper/editable-decimal-cell";
import PropTypes from "prop-types";
import { removeDuplicateHeaderRows } from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { GetPowerHistoricalData } from "../../../../../services/api/dashboard-api";
const DEFAULT_GROUPS = {
  Ongoing: true,
  Quiet: false,
  Closed: false,
  Dead: false,
};

const badgeColors = {
  Ongoing: "brand",
  Quiet: "informative",
  Closed: "success",
  Dead: "danger",
};

const GROUP_ORDER = ["Ongoing", "Quiet", "Closed", "Dead"];

export const isRowEdited = (original, edited) => {
  return (
    original.LastWeekHours !== edited.LastWeekHours ||
    original.WeekendHours !== edited.WeekendHours ||
    original.NextWeekHours !== edited.NextWeekHours
  );
};

export const getEditedRows = (data, originalData) =>
  data.filter((editedRow, index) => isRowEdited(originalData[index], editedRow));

export const renderWhenData = (dataLength, renderFunction) => {
  return dataLength === 0 ? () => null : renderFunction;
};

export const renderCellContent = (dataLength, emptyStateContent, filledContent) => {
  return dataLength === 0 ? emptyStateContent : filledContent;
};

export function makeDecimalCellHelper(visibleLength) {
  return (fieldName, nullFlagName) =>
    function ConditionalCell(props) {
      return renderCellContent(
        visibleLength,
        null,
        <EditableDecimalCell
          {...props}
          emptyAsNull
          disabled={props.dataItem.IsOverdue}
        />
      );
    };
}

export const getAccordionRowRender = (filteredData, groupState, toggleGroup, colspan, groupNameLabel = "GroupName", BadgeLabel = "Project") => {
  return (trElement, props) => {
    if (props.dataItem.isEmptyRow) return trElement;

    const groupName = props.dataItem[groupNameLabel];
    const isFirstInGroup =
      props.dataIndex ===
      filteredData.findIndex((item) => item.GroupName === groupName);
    const isVisible = groupState[groupName];

    if (!isFirstInGroup && !isVisible) return null;
    if (!isFirstInGroup) return isVisible ? trElement : null;

    const activeCount = filteredData.filter(
      (item) => item.GroupName === groupName
    ).length;

    return (
      <Fragment key={`group-${groupName}-${props.dataIndex}`}>
        <tr
          className="custom-group-header"
          style={{ backgroundColor: "#fff", height: "48px", borderBottom: "1px solid #edebe9", cursor: "pointer", lineHeight: "140%" }}
          onClick={() => toggleGroup(groupName)}
        >
          <td colSpan={colspan} style={{ padding: "0 16px", verticalAlign: "middle", borderBottom: "1px solid #edebe9", borderTop: "1px solid #edebe9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FluentButton
                className="accordion-toggle-button"
                disabled={false}
                icon={
                  <FluentUiIcon
                    name={isVisible ? "ChevronUpRegular" : "ChevronDownRegular"}
                    size={14}
                    disabled={false}
                  />
                }
                appearance="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupName);
                }}
              />
              <span style={{ fontWeight: "600", fontSize: "14px", marginLeft: "-10px" }}>
                {groupName}
              </span>

              <FluentBadge
                appearance="tint"
                size="large"
                color={badgeColors[groupName] || "brand"}
                text={`${activeCount} ${BadgeLabel}${activeCount > 1 ? "s" : ""}`}
              />
            </div>
          </td>
        </tr>
        {isVisible && trElement}
      </Fragment>
    );
  };
};

export const applySearchFilter = (searchValue, detailState, onDetailStateChange, setGroupState) => {
  const trimmed = searchValue.trim();

  if (!trimmed) {
    onDetailStateChange({
      ...detailState,
      filter: null,
      skip: 0,
    });

    setGroupState(DEFAULT_GROUPS);
    return;
  }

  const searchFilters = [
    { field: "ProjectName", operator: "contains", value: trimmed },
    { field: "ClientName", operator: "contains", value: trimmed },
  ];

  onDetailStateChange({
    ...detailState,
    filter: {
      logic: "or",
      filters: searchFilters,
    },
    skip: 0,
  });

  setGroupState({
    Ongoing: true,
    Quiet: true,
    Closed: true,
    Dead: true,
  });
};

const toDateSafe = (v) => (v instanceof Date ? v : v ? new Date(v) : null);
const toLocalMidnight = (v) => {
  const d = toDateSafe(v);
  return d && !isNaN(d.getTime()) ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
};

const rehydrateLastUpdatedFilters = (node) => {
  if (!node) return node;
  if (Array.isArray(node.filters)) {
    return { ...node, filters: node.filters.map(rehydrateLastUpdatedFilters) };
  }
  if (node.field === "LastUpdated") {
    return { ...node, value: toLocalMidnight(node.value) };
  }
  return node;
};

export const getFilteredSortedData = (flattenedData, filter, sort) => {
  let temp = flattenedData.map((r) => ({ ...r, LastUpdated: toLocalMidnight(r.LastUpdated) }));

  const memberFilters = [];
  (filter?.filters || []).forEach((f) => {
    if (f.field === "Members") {
      memberFilters.push((f.value ?? "").toLowerCase());
    } else if (Array.isArray(f.filters)) {
      f.filters.forEach((inner) => {
        if (inner.field === "Members") {
          memberFilters.push((inner.value ?? "").toLowerCase());
        }
      });
    }
  });

  if (memberFilters.length) {
    temp = temp.filter((item) =>
      item.MemberNamesArray?.some((name) => memberFilters.includes((name || "").toLowerCase()))
    );
  }

  const kendoCompatibleFilters =
    filter?.filters?.filter(
      (f) => f.field !== "Members" && !f.filters?.some((inner) => inner.field === "Members")
    ) ?? [];

  const processedFilter = rehydrateLastUpdatedFilters({ ...filter, filters: kendoCompatibleFilters });

  if (processedFilter?.filters?.length) {
    temp = filterBy(temp, processedFilter);
  }

  const sorted = sort?.length
    ? sort[0].field === "Members"
      ? [...temp].sort((a, b) => {
        const countA = a.MemberNamesArray?.length || 0;
        const countB = b.MemberNamesArray?.length || 0;
        return sort[0].dir === "asc" ? countA - countB : countB - countA;
      })
      : orderBy(temp, sort)
    : temp;

  const buckets = new Map(GROUP_ORDER.map((g) => [g, []]));
  const others = [];
  for (const row of sorted) (buckets.has(row.GroupName) ? buckets.get(row.GroupName) : others).push(row);

  return [...GROUP_ORDER.flatMap((g) => buckets.get(g)), ...others];
};

export const useMemberGroupToggle = (initialState = { Analyst: true, Associate: true }) => {
  const [groupState, setGroupState] = useState(initialState);

  const toggleGroup = useCallback((groupName) => {
    setGroupState((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  return { groupState, toggleGroup, setGroupState };
};

export const useGroupToggle = (initialState) => {
  const [groupState, setGroupState] = useState(() => ({
    ...DEFAULT_GROUPS,
    ...(initialState || {}),
  }));

  const toggleGroup = useCallback((groupName) => {
    const key = (groupName ?? "").trim();
    if (!key) return;

    setGroupState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const setAll = useCallback((value) => {
    setGroupState((prev) =>
      Object.fromEntries(Object.keys(prev).map((k) => [k, Boolean(value)]))
    );
  }, []);

  const resetDefaults = useCallback(() => setGroupState(DEFAULT_GROUPS), []);

  return { groupState, toggleGroup, setGroupState, setAll, resetDefaults };
};

// Submit Helpers
export const buildPowerPayload = (rows) => ({
  DealsToEdit: rows.map(
    ({ ProjectId, TaskId, LastWeekHours, WeekendHours, NextWeekHours }) => ({
      ProjectId,
      TaskId,
      LastWeekHours,
      WeekendHours,
      NextWeekHours,
    })
  )
});

export const mapPowerExportData = (data) => {
  return data.map((item) => {
    const owners = item.Members?.filter((m) => m.IsOwner)
      .map((m) => m.User?.Name)
      .filter(Boolean)
      .join(", ");

    const members = item.Members?.filter((m) => !m.IsOwner && !m?.IsStaffer)
      .map((m) => m.User?.Name)
      .filter(Boolean)
      .join(", ");

    return {
      ...item,
      Owners: owners || "-",
      Members: members || "-",
    };
  });
};

export const mapPowerProjectDrillDownExportData = (data, clientName, projectName, projectId) => {
  return data.map((item) => {
    return {
      ...item,
      ClientName: clientName || "-",
      ProjectName: projectName || "-",
      ProjectId: projectId || "-",
    };
  });
};

// Export Data Helpers
export const getDetailColumns = () => (
  <>
    <ExcelExportColumn field="ProjectId" title="ID" />
    <ExcelExportColumn field="ClientName" title="Client Name" />
    <ExcelExportColumn field="ProjectName" title="Project Name" />
    <ExcelExportColumn field="GroupName" title="Deal Status" />
    <ExcelExportColumn field="LastWeekHours" title="Hrs Spent Last Wk" />
    <ExcelExportColumn field="WeekendHours" title="Wknd Hrs Spent" />
    <ExcelExportColumn field="NextWeekHours" title="Est Hrs for Next Wk" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} />
  </>
);

export const getSummaryColumns = () => (
  <>
    <ExcelExportColumn field="User.UserName" title="Analyst/Associate" />
    <ExcelExportColumn field="OverallCapacity" title="Overall Capacity" />
    <ExcelExportColumn field="TotalHoursSpent" title="Total Hrs Spent" />
    <ExcelExportColumn field="TotalHoursEstimated" title="Total Hrs Est" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} />
  </>
);

export const getStafferProjectTracker = () => (
  <>
    <ExcelExportColumn field="ProjectId" title="ID" />
    <ExcelExportColumn field="ClientName" title="Client Name" />
    <ExcelExportColumn field="ProjectName" title="Project Name" />
    <ExcelExportColumn field="GroupName" title="Project Status" />
    <ExcelExportColumn field="ProjectType" title="Project Type" />
    <ExcelExportColumn field="TotalWeekHrs" title="Total Wk Hrs" />
    <ExcelExportColumn field="TotalWeekendHrs" title="Total Wknd Hrs" />
    <ExcelExportColumn field="Owners" title="Owner" />
    <ExcelExportColumn field="Members" title="Project Team" />
    <ExcelExportColumn field="CoverageSenior" title="Coverage Senior" />
    <ExcelExportColumn field="ProductSenior" title="Product Senior" />
    <ExcelExportColumn field="CoverageVP" title="Coverage VP" />
    <ExcelExportColumn field="CoverageDirector" title="Coverage Director" />
  </>
);

export const getStafferProjectTrackerMemberDetails = () => (
  <>
    <ExcelExportColumn field="ProjectId" title="ID" />
    <ExcelExportColumn field="ClientName" title="Client Name" />
    <ExcelExportColumn field="ProjectName" title="Project Name" />
    <ExcelExportColumn field="Banker" title="Analyst/Associate" />
    <ExcelExportColumn field="OverallCapacity" title="Overall Capacity" />
    <ExcelExportColumn field="LastWeekHrs" title="Hrs Spent Last Wk" />
    <ExcelExportColumn field="WeekendHours" title="Wknd Hrs Spent" />
    <ExcelExportColumn field="TotalHrs" title="Total Hrs" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Est. Hrs for Next Wk" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} />
  </>
);

export const getStafferMemberTracker = () => (
  <>
    <ExcelExportColumn field="Banker" title="Analyst/Associate" />
    <ExcelExportColumn field="GroupName" title="Role" />
    <ExcelExportColumn field="OverallCapacity" title="Overall Capacity" />
    <ExcelExportColumn field="LastWeekHrs" title="Hrs Spent Last Wk" />
    <ExcelExportColumn field="WeekendHours" title="Wknd Hrs Spent" />
    <ExcelExportColumn field="TotalHrs" title="Total Hrs" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Est. Hrs for Next Wk" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} />
  </>
);


export const mapPowerMemberData = (members) => {
  return members.map((item) => {
    const ongoingProjects = Array.isArray(item.Projects)
      ? item.Projects.filter(p => p.GroupName === "Ongoing")
      : [];
    const safeProjects = Array.isArray(item.Projects)
      ? item.Projects.filter(p => p.EstHoursForNextWeek != null)
      : [];
    return {
      ...item,
      LastWeekHrs: safeProjects?.reduce((sum, p) => sum + (p.LastWeekHrs || 0), 0) || 0,
      WeekendHours: safeProjects?.reduce((sum, p) => sum + (p.WeekendHours || 0), 0) || 0,
      TotalHrs: safeProjects?.reduce((sum, p) => sum + (p.TotalHrs || 0), 0) || 0,
      EstHoursForNextWeek: safeProjects?.reduce((sum, p) => sum + (p.EstHoursForNextWeek || 0), 0) || 0,
      ActiveNoOfDeals: safeProjects.length,
      ActiveDeals: safeProjects.map(d => d.ProjectName).join(", ") || "-",
      OngoingNoOfDeals: ongoingProjects.length
    };
  });
};
export const projectTrackerNewExportBankers = () => (
  <>
    <ExcelExportColumn field="Banker" title="Name" />
    <ExcelExportColumn field="OverallCapacity" title="Overall Capacity" />
    <ExcelExportColumn field="LastWeekHrs" title="Hrs Spent (Last Wk)" />
    <ExcelExportColumn field="WeekendHours" title="Wknd Hrs Spent (Last Wk)" />
    <ExcelExportColumn field="TotalHrs" title="Total Hrs (Last Wk)" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Current Wk Hrs" />
    <ExcelExportColumn field="OngoingNoOfDeals" title="# of Ongoing Deals" />
    <ExcelExportColumn field="ActiveNoOfDeals" title="# of Active Deals" />
    <ExcelExportColumn field="ActiveDeals" title="Active Deals" />
    {/* <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} /> */}
  </>
);

export const projectTrackerNewExportDeals = () => (
  <>
    <ExcelExportColumn field="ProjectName" title="Project Name" />
    <ExcelExportColumn field="ClientName" title="Client Name" />
    <ExcelExportColumn field="GroupName" title="Deal Status" />
    <ExcelExportColumn field="ProjectDescription" title="Project Description" />
    <ExcelExportColumn field="ProjectStart" title="Start Date" />
    <ExcelExportColumn field="ProjectEnd" title="Est End Date" />
    {/* <ExcelExportColumn field="Owners" title="Owner" /> */}
    <ExcelExportColumn field="Members" title="Project Team" />
    <ExcelExportColumn field="CoverageSenior" title="Coverage Senior" />
    <ExcelExportColumn field="ProductSenior" title="Product Senior" />
    <ExcelExportColumn field="CoverageDirector" title="Coverage Director" />
    <ExcelExportColumn field="CoverageVP" title="Coverage VP" />
    <ExcelExportColumn field="TotalLastWeekHrs" title="Last Wk Hrs" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Est. Hrs for the Upcoming Wk" />
  </>
);

export const sortMembersByRoleAndCapacity = (members) => {
  if (!Array.isArray(members)) {
    return [];
  }

  const ROLE_ORDER = ["Associate", "Analyst"];
  const CAPACITY_ORDER = ["High", "Medium", "Low"];

  return [...members].sort((a, b) => {
    const roleOrderA = ROLE_ORDER.indexOf(a.GroupName) !== -1 ? ROLE_ORDER.indexOf(a.GroupName) : 999;
    const roleOrderB = ROLE_ORDER.indexOf(b.GroupName) !== -1 ? ROLE_ORDER.indexOf(b.GroupName) : 999;

    if (roleOrderA !== roleOrderB) {
      return roleOrderA - roleOrderB;
    }

    const capacityOrderA = CAPACITY_ORDER.indexOf(a.OverallCapacity) !== -1 ? CAPACITY_ORDER.indexOf(a.OverallCapacity) : 999;
    const capacityOrderB = CAPACITY_ORDER.indexOf(b.OverallCapacity) !== -1 ? CAPACITY_ORDER.indexOf(b.OverallCapacity) : 999;

    return capacityOrderA - capacityOrderB;
  });
};

export const sortProjectsByStatus = (projects) => {
  if (!Array.isArray(projects)) {
    return [];
  }

  const GROUP_ORDER = ["Ongoing", "Quiet", "Closed", "Dead"];

  return [...projects].sort((a, b) => {
    const orderA = GROUP_ORDER.indexOf(a.GroupName) !== -1 ? GROUP_ORDER.indexOf(a.GroupName) : 999;
    const orderB = GROUP_ORDER.indexOf(b.GroupName) !== -1 ? GROUP_ORDER.indexOf(b.GroupName) : 999;
    return orderA - orderB;
  });
};

export const getStafferMemberProjectsTracker = () => (
  <>
    <ExcelExportColumn field="ProjectId" title="ID" />
    <ExcelExportColumn field="ClientName" title="Client Name" />
    <ExcelExportColumn field="ProjectName" title="Project Name" />
    <ExcelExportColumn field="GroupName" title="Project Status" />
    <ExcelExportColumn field="ProjectType" title="Project Type" />
    <ExcelExportColumn field="LastWeekHrs" title="Hrs Spent Last Wk" />
    <ExcelExportColumn field="WeekendHours" title="Wknd Hrs Spent" />
    <ExcelExportColumn field="TotalHrs" title="Total Hrs" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Est. Hrs for Next Wk" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" cellOptions={{ format: "mm/dd/yyyy" }} />
    <ExcelExportColumn field="Owners" title="Owner" />
    <ExcelExportColumn field="Members" title="Project Team" />
    <ExcelExportColumn field="CoverageSenior" title="Coverage Senior" />
    <ExcelExportColumn field="ProductSenior" title="Product Senior" />
    <ExcelExportColumn field="CoverageVP" title="Coverage VP" />
    <ExcelExportColumn field="CoverageDirector" title="Coverage Director" />
  </>
);

export const getMemberSummaryColumns = () => (
  <>
    <ExcelExportColumn field="Banker" title="Analyst/Associate" />
    <ExcelExportColumn field="OverallCapacity" title="Overall Capacity" />
    <ExcelExportColumn field="LastWeekHrs" title="Total Hrs Spent Last Wk" />
    <ExcelExportColumn field="WeekendHours" title="Total Wknd Hrs Spent" />
    <ExcelExportColumn field="EstHoursForNextWeek" title="Total Est. Hrs for Next Wk" />
  </>
);

export const getPowerTimeLogFileName = () => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  return `Banker Time Log ${timestamp}.xlsx`;
};

export const handleExport = async ({ summaryRef, detailRef }) => {
  if (!summaryRef.current || !detailRef.current) return;

  const fileName = getPowerTimeLogFileName();

  const summaryWorkbook = summaryRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Summary";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = detailRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferProjectTrackerExport = async ({ projectTrackerRef, memberDetailRef }) => {
  if (!projectTrackerRef.current || !memberDetailRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Staffer Project Tracker Log ${timestamp}.xlsx`;

  const summaryWorkbook = projectTrackerRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Project Tracker";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = memberDetailRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Project Member Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferProjectMemberDetailsTrackerExport = async ({ jBRef, ProjectName, ClientName }) => {
  if (!jBRef.current) return;

  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `${ProjectName} - ${ClientName} Member Details ${timestamp}.xlsx`;

  const summaryWorkbook = jBRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Project Member Details";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferMemberTrackerExport = async ({ _juniorBankersExportRef, _memberProjectsExportRef }) => {
  if (!_juniorBankersExportRef.current || !_memberProjectsExportRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Staffer Member Tracker Log ${timestamp}.xlsx`;

  const summaryWorkbook = _juniorBankersExportRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Member Tracker";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = _memberProjectsExportRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Member Project Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferMemberProjectTrackerExport = async ({ jBRef, projectRef, Banker }) => {
  if (!jBRef.current || !projectRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `${Banker} Time Tracker Log ${timestamp}.xlsx`;

  const summaryWorkbook = jBRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Summary";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = projectRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Member Project Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Local Storage Helpers
export const getStorageKey = (key, userId) => `${key}_user_${userId}`;

export const getInitialDetailState = (userId, baseKey = "powerBanker") => {
  if (!userId) return { sort: [], filter: null };

  try {
    const sortRaw = localStorage.getItem(getStorageKey(`${baseKey}Sort`, userId));
    const filterRaw = localStorage.getItem(getStorageKey(`${baseKey}Filter`, userId));
    const sort = sortRaw ? JSON.parse(sortRaw) : [];
    const filter = filterRaw ? JSON.parse(filterRaw) : null;
    return { sort, filter };
  } catch (e) {
    console.warn("Failed to parse localStorage", e);
    return { sort: [], filter: null };
  }
};

export const saveDetailStateToStorage = (userId, newState, searchValue, baseKey = "powerBanker") => {
  if (!userId || searchValue?.trim()) return;
  localStorage.setItem(getStorageKey(`${baseKey}Sort`, userId), JSON.stringify(newState.sort));
  localStorage.setItem(getStorageKey(`${baseKey}Filter`, userId), JSON.stringify(newState.filter));
};

export const clearDetailStateFromStorage = (userId, baseKey = "powerBanker") => {
  if (!userId) return;
  localStorage.removeItem(getStorageKey(`${baseKey}Sort`, userId));
  localStorage.removeItem(getStorageKey(`${baseKey}Filter`, userId));
};

// Helper Components
export const ProjectIdCell = ({ dataItem, canEdit, isNewDeal }) => (
  <td className="badge-edit-cell background-white k-table-td">
    <div className="badge-edit-content" style={{ pointerEvents: 'none' }}>
      <PowerIdBadge text={dataItem.ProjectIdString} groupName={dataItem.GroupName} />
      {
        isNewDeal && (
          <FluentBadge
            appearance="filled"
            size="medium"
            color="success"
            text="New"
            shape="rounded"
          />
        )
      }
      {canEdit && dataItem && (
        <EditIconButton
          projectId={dataItem.ProjectId}
          projectName={dataItem.ProjectName}
        />
      )}
    </div>
  </td>
);

export const EditIconButton = ({ projectId, projectName }) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const history = useHistory();

  return (
    <div className={`edit-button-wrapper ${tooltipVisible ? "tooltip-open" : ""}`}>
      <Tooltip
        content={
          <span>
            Edit <span style={{ fontWeight: 500 }}>{projectName}</span> Project
          </span>
        }
        relationship="label"
        positioning="above"
        onVisibleChange={(_, data) => setTooltipVisible(data.visible)}
      >
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ display: "inline-block" }}
        >
          <FluentButton
            icon={
              <FluentUiIcon
                name={hovered ? "EditFilled" : "EditRegular"}
                color="#0F6CBD"
                size={20}
              />
            }
            onClick={() =>
              history.push({
                pathname: `/projects/edit/${projectId}`,
                state: { fromDashboard: true },
              })
            }
            appearance="transparent"
            className="edit-hover-button"
          />
        </div>
      </Tooltip>
    </div>
  );
};

export const PowerBankerEmptyState = ({ canCreate }) => {
  const history = useHistory();

  return (
    <div className="empty-state-container">
      <img src={EmptyState} alt="No Data" />
      <p className="empty-state-header">
        Let's get you on a Deal!{" "}
        <FluentUiIcon name="Rocket24Filled" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        Any Deals you're a Member of will show up here. Start a{" "}
        {canCreate ? (
          <button
            onClick={() =>
              history.push({
                pathname: "/projects/create",
                state: { fromDashboard: true },
              })
            }
            className="create-deal-link"
          >
            new one
          </button>
        ) : (
          <span className="create-deal-link disabled" aria-disabled="true">new one</span>
        )}{" "}
        or ping your Staffer to join an existing Deal.
      </p>
    </div>
  );
};

export const PowerStafferEmptyState = ({ canCreate }) => {
  const history = useHistory();

  return (
    <div className="empty-state-container">
      <img src={EmptyState} alt="No Data" />
      <p className="empty-state-header">
        No Active Deals on TRIYO yet.
      </p>
      <p className="empty-state-subtext">
        Any Deals created will show up here. Start a{" "}
        {canCreate ? (
          <button
            onClick={() =>
              history.push({
                pathname: "/projects/create",
                state: { fromDashboard: true },
              })
            }
            className="create-deal-link"
          >
            new one
          </button>
        ) : (
          <span className="create-deal-link disabled" aria-disabled="true">new one</span>
        )}
      </p>
    </div>
  );
};

export const PowerStafferMemberProjectsEmptyState = ({ canCreate }) => {
  const history = useHistory();

  return (
    <div className="empty-state-container">
      <img src={EmptyState} alt="No Data" />
      <p className="empty-state-header">
        Let's get you on a Deal!{" "}
        <FluentUiIcon name="Rocket24Filled" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        This member is not part of any deals. Start a{" "}
        {canCreate ? (
          <button
            onClick={() =>
              history.push({
                pathname: "/projects/create",
                state: { fromDashboard: true },
              })
            }
            className="create-deal-link"
          >
            new one
          </button>
        ) : (
          <span className="create-deal-link disabled" aria-disabled="true">new one</span>
        )}{" "}
      </p>
    </div>
  );
};

export const PowerBankerNoDealOnSearchState = ({ resetDataState, refineSearch }) => {
  return (
    <div className="empty-state-container">
      <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
      <p className="empty-state-header">
        Hmm, nothing matched!{" "}
        <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        We couldn’t find any Deals you’re a Member of based on your search.<br /><br />
        You can{" "}
        <button
          type="button"
          onClick={resetDataState}
          className="create-deal-link"
        >
          reset your search
        </button>{" "} OR try{" "}
        <button
          className="create-deal-link"
          onClick={refineSearch}
        >
          refining your keywords
        </button>{"."}
      </p>
    </div>
  );
};

export const PowerBankerNoDealOnFilterState = ({ resetDataState }) => {
  return (
    <div className="empty-state-container">
      <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
      <p className="empty-state-header">
        No results found with the current filters!{" "}
        <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        Try{" "}
        <button
          type="button"
          onClick={resetDataState}
          className="create-deal-link"
        >
          adjusting the filter options
        </button>{" "}
        to refine your results.
      </p>
    </div>
  );
};

export const PowerIdBadge = ({ text, groupName }) => {
  const color = badgeColors[groupName] || "informative";
  return (
    <FluentBadge
      shape="rounded"
      appearance="tint"
      size="medium"
      color={color}
      text={text}
    />
  );
};

export const PowerCapacity = ({ capacity }) => {
  return (
    <div className={`${capacity} power-capacity`} style={{ pointerEvents: 'none' }}>
      <FluentBadge
        shape="rounded"
        appearance="tint"
        size="extra-large"
        text={capacity?.charAt(0)?.toUpperCase()}
      ></FluentBadge>
    </div>
  );
};

export const OverallCapacityCellNonEditable = ({ value }) => {
  const normalized = (value || "");
  const isValid = ["Low", "Medium", "High"].includes(normalized);
  const colorMap = { Low: "#D83B01", Medium: "#0F6CBD", High: "#107C10" };
  const iconMap = {
    Low: "Fire20Filled",
    Medium: "TextAsterisk20Filled",
    High: "BowlChopsticks20Filled",
  };
  return (
    <div className={`oc-wrap ${isValid ? `oc-${normalized}` : ""}`}>
      {isValid && (
        <span className="oc-trigger-label-non-editable" aria-hidden="true">
          <FluentUiIcon
            name={iconMap[normalized]}
            size={16}
            color={colorMap[normalized]}
          />
          {normalized}
        </span>
      )}
    </div>
  );
};

export const OverallCapacityCell = ({ value, taskExists, onChange }) => {
  const disabled = !taskExists;
  const normalized = (value || "");
  const isValid = ["Low", "Medium", "High"].includes(normalized);
  const selectedOptions = isValid ? [normalized] : [];

  const labelMap = { Low: "Low", Medium: "Medium", High: "High" };
  const colorMap = { Low: "#D83B01", Medium: "#0F6CBD", High: "#107C10" };
  const iconMap = {
    Low: "Fire20Filled",
    Medium: "TextAsterisk20Filled",
    High: "BowlChopsticks20Filled",
  };

  return (
    <Tooltip
      content="Please reach out to your Staffer for tracking your Overall Capacity"
      relationship="label"
      withArrow
      positioning="above"
      visible={disabled ? undefined : false}
    >
      <div
        className={`oc-wrap ${isValid ? `oc-${normalized}` : ""}`}
        style={{ position: "relative", minWidth: 180, width: "100%" }}
      >
        {isValid && (
          <span className="oc-trigger-icon" aria-hidden="true">
            <FluentUiIcon
              name={iconMap[normalized]}
              size={16}
              color={colorMap[normalized]}
            />
          </span>
        )}

        {isValid && (
          <span className="oc-trigger-label" aria-hidden="true">
            {labelMap[normalized]}
          </span>
        )}

        <Dropdown
          aria-label="Overall Capacity"
          disabled={disabled}
          placeholder={taskExists && !isValid ? "Please select" : undefined}
          selectedOptions={selectedOptions}
          appearance="outline"
          onOptionSelect={(_, data) => {
            if (!disabled && data.optionValue) onChange?.(String(data.optionValue));
          }}
        >
          <Option value="Low" text={labelMap.Low}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FluentUiIcon name={iconMap.Low} size={16} color={colorMap.Low} />
              {labelMap.Low}
            </span>
          </Option>
          <Option value="Medium" text={labelMap.Medium}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FluentUiIcon name={iconMap.Medium} size={16} color={colorMap.Medium} />
              {labelMap.Medium}
            </span>
          </Option>
          <Option value="High" text={labelMap.High}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <FluentUiIcon name={iconMap.High} size={16} color={colorMap.High} />
              {labelMap.High}
            </span>
          </Option>
        </Dropdown>
      </div>
    </Tooltip>
  );
};

export const renderAvatarWithName = (name, color) => {
  if (!name || name === "—") return "—";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Avatar
        name={name}
        color={color}
        size={24}
      />
      <span>{name}</span>
    </div>
  );
};

export const handleExportGroupedPower = async ({ projects, members, projectColumnsGrouped, memberColumnsGrouped }) => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Power Banker Summary ${timestamp}.xlsx`;

  // Extract {field, title} from JSX ExcelExportColumn children
  const extractColumns = (columnsJSX) => {
    if (!columnsJSX) return [];
    if (Array.isArray(columnsJSX)) return columnsJSX;
    const children = columnsJSX.props?.children;
    const childArray = Array.isArray(children) ? children : [children];
    return childArray
      .filter(Boolean)
      .map(child => ({ field: child.props?.field, title: child.props?.title }))
      .filter(col => col.field);
  };

  const memberCols = extractColumns(memberColumnsGrouped);
  const projectCols = extractColumns(projectColumnsGrouped);

  // ── Sheet 1: Banker Summary ──────────────────────────────────────
  const ROLE_ORDER = ["Analyst", "Associate"];
  const CAPACITY_ORDER = ["High", "Medium", "Low"];
  const ROLE_LABEL = { Analyst: "Analysts", Associate: "Associates" };

  const sortedMembers = [...(Array.isArray(members) ? members : [])].sort((a, b) => {
    const rA = ROLE_ORDER.indexOf(a.GroupName) !== -1 ? ROLE_ORDER.indexOf(a.GroupName) : 999;
    const rB = ROLE_ORDER.indexOf(b.GroupName) !== -1 ? ROLE_ORDER.indexOf(b.GroupName) : 999;
    if (rA !== rB) return rA - rB;
    const cA = CAPACITY_ORDER.indexOf(a.OverallCapacity) !== -1 ? CAPACITY_ORDER.indexOf(a.OverallCapacity) : 999;
    const cB = CAPACITY_ORDER.indexOf(b.OverallCapacity) !== -1 ? CAPACITY_ORDER.indexOf(b.OverallCapacity) : 999;
    return cA - cB;
  });

  const groupedMembers = {};
  sortedMembers.forEach(member => {
    const role = member.GroupName || "Other";
    if (!groupedMembers[role]) groupedMembers[role] = [];
    groupedMembers[role].push(member);
  });

  const SECTION_HEADER_BG = "#2B2B2B";
  const CAPACITY_BG = { High: "#daf2d0", Medium: "#ffffc4", Low: "#FFCDD2" };

  const memberRows = [
    { cells: memberCols.map(col => ({ value: col.title, bold: true, background: "#D3D3D3" })) }
  ];

  [...ROLE_ORDER, ...Object.keys(groupedMembers).filter(r => !ROLE_ORDER.includes(r))].forEach(role => {
    const items = groupedMembers[role];
    if (!items?.length) return;
    memberRows.push({
      cells: [
        { value: ROLE_LABEL[role] || role, bold: true, background: SECTION_HEADER_BG, color: "#FFFFFF" },
        ...memberCols.slice(1).map(() => ({ value: "", background: SECTION_HEADER_BG }))
      ]
    });
    items.forEach(member => {
      memberRows.push({
        cells: memberCols.map(col => {
          const cell = { value: member[col.field] };
          if (col.field === "OverallCapacity" && CAPACITY_BG[member.OverallCapacity]) {
            cell.background = CAPACITY_BG[member.OverallCapacity];
          }
          return cell;
        })
      });
    });
  });

  // ── Sheet 2: Deal Details ────────────────────────────────────────
  const sectionColors = [
    "#E8EBFA", "#FDF5FC", "#EBF3FC", "#F1FAF1",
    "#FDF6F3", "#FEFBF4", "#F5F5F5", "#FFF9E6",
  ];

  const PROJECT_TYPE_ORDER = [
    "Live - Sell-side", "Live - Buyside", "Live - Equity", "Live - Financing",
    "Pitch - M&A", "Pitch - Equity", "Pitch - Financing", "Coverage Meeting",
  ];
  const DEAL_STATUS_ORDER = ["Ongoing", "Quiet", "Closed", "Dead"];

  const safeProjects = Array.isArray(projects)
    ? projects.filter(p => p.GroupName === "Ongoing" || p.GroupName === "Quiet")
    : [];
  const groupedProjects = {};
  safeProjects.forEach(project => {
    const type = project.ProjectType || "Unknown";
    if (!groupedProjects[type]) groupedProjects[type] = [];
    groupedProjects[type].push(project);
  });

  // Sort items within each group by deal status, then by Est Hrs descending
  Object.values(groupedProjects).forEach(items => {
    items.sort((a, b) => {
      const sA = DEAL_STATUS_ORDER.indexOf(a.GroupName) !== -1 ? DEAL_STATUS_ORDER.indexOf(a.GroupName) : 999;
      const sB = DEAL_STATUS_ORDER.indexOf(b.GroupName) !== -1 ? DEAL_STATUS_ORDER.indexOf(b.GroupName) : 999;
      if (sA !== sB) return sA - sB;
      return (Number(b.EstHoursForNextWeek) || 0) - (Number(a.EstHoursForNextWeek) || 0);
    });
  });

  const lastWeekHrsIdx = projectCols.findIndex(col => col.field === "TotalLastWeekHrs");
  const estHrsIdx = projectCols.findIndex(col => col.field === "EstHoursForNextWeek");

  const projectRows = [
    { cells: projectCols.map(col => ({ value: col.title, bold: true, background: "#D3D3D3" })) }
  ];

  let colorIndex = 0;
  let grandTotalLastWeekHrs = 0;
  let grandTotalEstHrs = 0;
  const orderedTypes = [
    ...PROJECT_TYPE_ORDER.filter(t => groupedProjects[t]),
    ...Object.keys(groupedProjects).filter(t => !PROJECT_TYPE_ORDER.includes(t)),
  ];

  orderedTypes.forEach(type => {
    const items = groupedProjects[type];
    const sectionColor = sectionColors[colorIndex % sectionColors.length];
    colorIndex++;

    projectRows.push({
      cells: [
        { value: `Project Type: ${type}`, bold: true, background: sectionColor },
        ...projectCols.slice(1).map(() => ({ value: "", background: sectionColor }))
      ]
    });

    items.forEach(project => {
      projectRows.push({
        cells: projectCols.map(col => {
          const cell = { value: project[col.field] };
          if (col.field === "ProjectStart" || col.field === "ProjectEnd") {
            const raw = project[col.field];
            cell.value = raw ? dayjs(raw).format("D-MMM-YYYY") : null;
          }
          return cell;
        })
      });
    });

    const sumLastWeekHrs = items.reduce((acc, row) => acc + (Number(row.TotalLastWeekHrs) || 0), 0);
    const sumEstHrs = items.reduce((acc, row) => acc + (Number(row.EstHoursForNextWeek) || 0), 0);
    grandTotalLastWeekHrs += sumLastWeekHrs;
    grandTotalEstHrs += sumEstHrs;

    projectRows.push({
      cells: projectCols.map((col, idx) => {
        if (idx === 0) return { value: "Subtotal", bold: true, background: sectionColor };
        if (idx === lastWeekHrsIdx && lastWeekHrsIdx !== -1) return { value: sumLastWeekHrs, bold: true, background: sectionColor };
        if (idx === estHrsIdx && estHrsIdx !== -1) return { value: sumEstHrs, bold: true, background: sectionColor };
        return { value: "", background: sectionColor };
      })
    });
  });

  const GRAND_TOTAL_BG = "#D3D3D3";
  projectRows.push({
    cells: projectCols.map((col, idx) => {
      if (idx === lastWeekHrsIdx - 1 && lastWeekHrsIdx > 0) return { value: "Totals", bold: true, background: GRAND_TOTAL_BG };
      if (idx === lastWeekHrsIdx && lastWeekHrsIdx !== -1) return { value: grandTotalLastWeekHrs, bold: true, background: GRAND_TOTAL_BG };
      if (idx === estHrsIdx && estHrsIdx !== -1) return { value: grandTotalEstHrs, bold: true, background: GRAND_TOTAL_BG };
      return { value: "", background: GRAND_TOTAL_BG };
    })
  });

  // ── Build & download workbook ────────────────────────────────────
  const workbook = {
    sheets: [
      {
        title: "Banker Summary",
        columns: memberCols.map(col => ({ ...col, autoWidth: true })),
        rows: memberRows,
        frozenRows: 1,
      },
      {
        title: "Deal Details",
        columns: projectCols.map(col => ({ ...col, autoWidth: true })),
        rows: projectRows,
        frozenRows: 1,
      }
    ]
  };
  try {
    const dataUrl = await toDataURL(workbook);
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  catch (error) {
    console.error("Export failed", error);
  }
};

export const ExportHistoricalData = async (startDate, endDate) => {
  console.log("ExportHistoricalData called with: ", { startDate, endDate });
  const payload = {
    StartDate: startDate,
    EndDate: endDate,
  };
  try {
    const res = await GetPowerHistoricalData(payload);
    console.log("Historical Data Export Response: ", res);

    if (res.status === 200) {
      const link = document.createElement("a");
      link.href = res.data;
      link.download = "rawData.xlsx";
      link.click();
    }
    return res;
  } catch (err) {
    console.log("Error [GetPowerHistoricalData] => ", err);
    throw err;
  }
};

PowerIdBadge.propTypes = {
  text: PropTypes.string.isRequired,
  groupName: PropTypes.string.isRequired,
};

ProjectIdCell.propTypes = {
  dataItem: PropTypes.shape({
    ProjectId: PropTypes.number.isRequired,
    ProjectIdString: PropTypes.string.isRequired,
    ProjectName: PropTypes.string.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  isNewDeal: PropTypes.bool.isRequired,
};

EditIconButton.propTypes = {
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
};

PowerBankerNoDealOnSearchState.propTypes = {
  resetDataState: PropTypes.func.isRequired,
  refineSearch: PropTypes.func.isRequired,
};

PowerBankerNoDealOnFilterState.propTypes = {
  resetDataState: PropTypes.func.isRequired,
};

PowerBankerEmptyState.propTypes = {
  canCreate: PropTypes.bool.isRequired,
};

OverallCapacityCell.propTypes = {
  value: PropTypes.string,
  taskExists: PropTypes.bool,
  onChange: PropTypes.func,
};

PowerStafferEmptyState.propTypes = {
  canCreate: PropTypes.bool.isRequired,
};

PowerStafferMemberProjectsEmptyState.propTypes = {
  canCreate: PropTypes.bool.isRequired,
};

OverallCapacityCellNonEditable.propTypes = {
  value: PropTypes.string
};

PowerCapacity.propTypes = {
  capacity: PropTypes.string,
};